import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod/v4';
import type { Profile, UserSettings } from '@/types/types';
import type { ProfileCacheEntry } from './types';
import { validateMagicBytes } from '../imageUtils';
import { ASSET_BUCKET, storagePathFromValue, removeStorageObject } from './storage';

// Zod schema for allowed settings keys -- strips unknown properties
export const UserSettingsSchema = z.object({
    language: z.string().optional(),
    timezone: z.string().optional(),
    auth: z.object({ has_password_linked: z.boolean().optional() }).optional(),
    // `removed` is the record of a deliberate "no picture" choice. Without it a
    // Google user who removes their avatar gets it back on the next mount, because
    // the session still carries the Google URL and the bootstrap re-persists it.
    avatar: z.object({ removed: z.boolean().optional() }).optional(),
    display: z.object({ show_shortcut_hints: z.boolean().optional(), sidebar_collapsed: z.boolean().optional() }).optional(),
    features: z.object({ identity_system_enabled: z.boolean().optional() }).optional(),
    pulse: z.record(z.string(), z.unknown()).optional(),
    insights: z.record(z.string(), z.unknown()).optional(),
    notifications: z.record(z.string(), z.unknown()).optional(),
    email: z.record(z.string(), z.unknown()).optional(),
}).strip();

export async function getProfile(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
): Promise<Profile | null> {
    const cached = getCache();
    if (cached && Date.now() < cached.expiresAt) {
        return cached.data;
    }
    const { data, error } = await client
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

    if (error) {
        console.error('Error fetching profile:', error.message);
        return null;
    }
    setCache({ data, expiresAt: Date.now() + 5000 });
    return data;
}

export async function getSystemSettings(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
): Promise<{ days: number; cycles: number }> {
    const profile = await getProfile(client, userId, getCache, setCache);
    return {
        days: profile?.version_days_default || 90,
        cycles: profile?.cycles_per_version_default || 6
    };
}

export async function updateProfile(
    client: SupabaseClient,
    userId: string,
    updates: Partial<Profile>,
    invalidateProfile: () => void,
): Promise<void> {
    const { error } = await client
        .from('profiles')
        .update(updates)
        .eq('id', userId);
    if (error) throw error;
    invalidateProfile();
}

export async function completeOnboarding(
    client: SupabaseClient,
    userId: string,
    invalidateProfile: () => void,
): Promise<void> {
    await updateProfile(client, userId, { has_onboarded: true } as Partial<Profile>, invalidateProfile);
}

export async function completeTour(
    client: SupabaseClient,
    userId: string,
    invalidateProfile: () => void,
): Promise<void> {
    await updateProfile(client, userId, { has_toured: true } as Partial<Profile>, invalidateProfile);
}

export async function updateSystemSettings(
    client: SupabaseClient,
    userId: string,
    days: number,
    cycles: number,
): Promise<void> {
    const { error } = await client
        .from('profiles')
        .update({ version_days_default: days, cycles_per_version_default: cycles })
        .eq('id', userId);
    if (error) throw error;
}

export async function getUserSettings(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
): Promise<UserSettings> {
    const profile = await getProfile(client, userId, getCache, setCache);
    const raw = profile?.settings || {};
    const parsed = UserSettingsSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
}

export async function updateUserSettings(
    client: SupabaseClient,
    userId: string,
    settings: Partial<UserSettings>,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
    invalidateProfile: () => void,
): Promise<void> {
    const current = await getUserSettings(client, userId, getCache, setCache);
    // Deep-merge nested objects so callers can pass partial sub-keys
    const shallow = { ...current, ...settings };
    for (const key of Object.keys(settings) as (keyof typeof settings)[]) {
        const cur = current[key];
        const inc = settings[key];
        if (cur && inc && typeof cur === 'object' && typeof inc === 'object' && !Array.isArray(cur)) {
            (shallow as Record<string, unknown>)[key] = { ...cur, ...inc };
        }
    }
    const parsed = UserSettingsSchema.safeParse(shallow);
    if (!parsed.success) throw new Error('Invalid settings: ' + parsed.error.issues[0]?.message);
    const merged = parsed.data;
    const profile = await getProfile(client, userId, getCache, setCache);
    const resolvedUserId = profile?.id || userId;
    const { error } = await client
        .from('profiles')
        .update({ settings: merged })
        .eq('id', resolvedUserId);
    if (error) throw error;
    invalidateProfile();
}

export async function uploadAvatar(
    client: SupabaseClient,
    userId: string,
    file: File,
): Promise<string> {
    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

    const fileExt = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
        throw new Error(`Invalid file type: .${fileExt}. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        throw new Error(`Invalid MIME type: ${file.type}`);
    }
    if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File too large: ${Math.round(file.size / 1024)}KB. Max: 2MB`);
    }
    if (!await validateMagicBytes(file)) {
        throw new Error('File content does not match its declared type.');
    }

    // Read the outgoing avatar BEFORE the upload so it can be cleaned up after.
    const { data: prior } = await client
        .from('profiles')
        .select('avatar_url')
        .eq('id', userId)
        .maybeSingle();

    const fileName = `${userId}/avatar-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await client.storage
        .from(ASSET_BUCKET)
        .upload(filePath, file, { upsert: true });

    if (uploadError) throw uploadError;

    // Every save mints a fresh timestamped name, so without this the previous
    // avatar is abandoned in a PUBLIC bucket on every single change — the one
    // unbounded leak in the app. Best effort and after the upload succeeded:
    // losing the new avatar to a cleanup error would be far worse than leaving
    // one orphan behind, which tools/storage-orphans.mjs can still collect.
    const previousPath = storagePathFromValue(prior?.avatar_url);
    if (previousPath && previousPath !== filePath) {
        await removeStorageObject(client, previousPath);
    }

    const { data } = client.storage
        .from(ASSET_BUCKET)
        .getPublicUrl(filePath);

    if (!data?.publicUrl) {
        throw new Error('Upload succeeded but the public URL could not be resolved.');
    }
    return data.publicUrl;
}

/**
 * Clear the profile picture without replacing it.
 *
 * The stored object is deleted, not just unlinked: the bucket is PUBLIC, so a
 * row update alone would leave the picture the user just removed fetchable at
 * its URL forever. storagePathFromValue returns '' for a Google-hosted URL, so
 * a bootstrapped Google avatar unlinks without a delete attempt against a
 * bucket that never held it.
 */
export async function removeAvatar(
    client: SupabaseClient,
    userId: string,
): Promise<void> {
    const { data: prior } = await client
        .from('profiles')
        .select('avatar_url')
        .eq('id', userId)
        .maybeSingle();

    const { error } = await client
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', userId);
    if (error) throw error;

    await removeStorageObject(client, storagePathFromValue(prior?.avatar_url));
}

export async function getStorageUsage(
    client: SupabaseClient,
    userId: string,
): Promise<{ files: { name: string; size: number }[]; totalBytes: number }> {
    const { data: files, error } = await client.storage
        .from(ASSET_BUCKET)
        .list(userId, { limit: 100 });
    if (error) throw error;
    const items = (files || [])
        .filter(f => f.name !== '.emptyFolderPlaceholder')
        .map(f => ({ name: f.name, size: (f.metadata as { size?: number })?.size || 0 }));
    const totalBytes = items.reduce((sum, f) => sum + f.size, 0);
    return { files: items, totalBytes };
}

// Estimated average bytes per row for each table
export const ROW_SIZE_ESTIMATES: Record<string, number> = {
    versions: 512,        // title, persona, dates, metadata
    cycles: 2048,         // JSONB arrays (priorities, problems, cch_list, mantras)
    habits: 256,          // name, weight, category, refs
    goals: 1024,          // title, type, subtasks JSONB
    habit_logs: 64,       // habit_id, date, boolean
    slides: 256,          // category, caption, url (image bytes counted separately)
    archives: 4096,       // markdown content can be long
    mantras: 128,         // short text
    identity_items: 256,  // name, type, description
};

export async function getDataRecordCounts(
    client: SupabaseClient,
    userId: string,
): Promise<{ counts: Record<string, number>; estimatedBytes: number }> {
    const tables = Object.keys(ROW_SIZE_ESTIMATES);
    const counts: Record<string, number> = {};
    let estimatedBytes = 0;
    await Promise.all(
        tables.map(async (table) => {
            const { count } = await client.from(table).select('*', { count: 'exact', head: true }).eq('user_id', userId);
            const c = count ?? 0;
            counts[table] = c;
            estimatedBytes += c * (ROW_SIZE_ESTIMATES[table] || 128);
        })
    );
    return { counts, estimatedBytes };
}

export async function changePassword(
    client: SupabaseClient,
    newPassword: string,
): Promise<void> {
    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) throw error;
}

export async function getAuthSessionUser(
    client: SupabaseClient,
): Promise<{ email: string; name: string } | null> {
    const { data: { session } } = await client.auth.getSession();
    if (!session?.user) return null;
    return {
        email: session.user.email || '',
        name: session.user.user_metadata?.full_name || '',
    };
}

export async function verifyEmailToken(
    client: SupabaseClient,
    token: string,
    email: string,
): Promise<{ success: boolean; message: string }> {
    const { data, error } = await client.rpc('verify_token', {
        p_token: token,
        p_email: email,
    });
    if (error) throw error;
    if (data?.[0]) return data[0];
    return { success: false, message: 'An unexpected error occurred during verification.' };
}
